import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Button, Card, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip,
  Skeleton, Stack, Typography, Snackbar, Autocomplete, Checkbox,
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, SecurityOutlined as SecurityIcon, CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from '@mui/icons-material';

// Components
import { PageHeader } from '@/common-components/layout';

// Hooks
import { useRoles, usePermissions, useUrlPagination } from '@/common-hooks';
import { useAuthentication } from '@/common-hooks/use-authentication';

// Types
import type { Role } from '@/common-types';

// Utilities
import { FetchError } from '@/common-utilities/fetch-utilities';
import { PERMISSIONS } from '@/common-constants/permissions';

export default function Roles() {
  const { hasPermission } = useAuthentication();
  const canManageUsers = hasPermission(PERMISSIONS.USER_MANAGEMENT);
  const { roles, loading, error, refresh, createRole, updateRole, deleteRole } = useRoles();
  const { permissions, loading: permissionsLoading, refresh: refreshPermissions } = usePermissions();
  
  const { page, rowsPerPage, setPage, setRowsPerPage } = useUrlPagination('roles');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRole, setNewRole] = useState({ roleId: '', name: '', description: '', permissions: [] as string[] });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Role | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  // Edit modal state
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);

  const fetchingRef = useRef(false);
  useEffect(() => {
    if (canManageUsers && !fetchingRef.current) {
      fetchingRef.current = true;
      refresh().finally(() => { fetchingRef.current = false; });
    }
  }, [canManageUsers, refresh]);

  const paginatedRoles = useMemo(() => 
    roles.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [roles, page, rowsPerPage]
  );

  const handleRefresh = () => {
    refresh();
    refreshPermissions();
  };

  const handleCreateClick = () => {
    setShowCreateModal(true);
    // Fetch permissions only if not already loaded
    if (permissions.length === 0) {
      refreshPermissions();
    }
  };

  const handleEditClick = (role: Role) => {
    setSelectedRole(role);
    setEditName(role.name);
    setEditDescription(role.description || '');
    setSelectedPermissions(role.permissions || []);
    setShowEditModal(true);
    // Fetch permissions only if not already loaded
    if (permissions.length === 0) {
      refreshPermissions();
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole || !editName) return;
    try {
      setUpdating(true);
      await updateRole(selectedRole.roleId, { 
        name: editName, 
        description: editDescription, 
        permissions: selectedPermissions 
      });
      setShowEditModal(false);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.roleId || !newRole.name) {
      setLocalError('Role ID and name are required');
      return;
    }
    try {
      setCreating(true);
      await createRole({
        roleId: newRole.roleId,
        name: newRole.name,
        description: newRole.description,
        permissions: newRole.permissions,
      });
      setShowCreateModal(false);
      setNewRole({ roleId: '', name: '', description: '', permissions: [] });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(deleteConfirm.roleId);
      await deleteRole(deleteConfirm.roleId);
      setDeleteConfirm(null);
    } catch (err) {
      // Check if it's a 400 error (bad request) - show toast message
      if (err instanceof FetchError && err.status === 400) {
        // Extract error message from the response
        const errorMessage = err.message || 'Failed to delete role';
        setToastMessage(errorMessage);
        setDeleteConfirm(null);
      } else {
        // For other errors, show in alert
        setLocalError(err instanceof Error ? err.message : 'Failed to delete');
      }
    } finally {
      setDeleting(null);
    }
  };

  if (!canManageUsers) {
    return <Alert severity="warning">You need user management permission to access this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Roles"
        subtitle={`${roles.length} roles`}
        actions={
          <>
            <Button variant="contained" size="small" startIcon={<AddIcon fontSize="small" />} onClick={handleCreateClick}>
              Create
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {error || localError}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table 
            size="small"
            sx={{ 
              tableLayout: 'auto', // Auto-size columns based on content
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Role ID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Description</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Created</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={180} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={30} /></TableCell>
                  </TableRow>
                ))
              ) : paginatedRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <SecurityIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No roles</Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={handleCreateClick} sx={{ mt: 1 }}>Create</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRoles.map((role) => (
                  <TableRow key={role.roleId} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={role.roleId} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} /></TableCell>
                    <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{role.name}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role.description || '-'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{new Date(role.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditClick(role)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(role)} disabled={deleting === role.roleId}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {roles.length > 5 && (
          <TablePagination
            component="div"
            count={roles.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Card>

      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Role</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField label="Role ID" value={newRole.roleId} onChange={(e) => setNewRole({ ...newRole, roleId: e.target.value })} size="small" fullWidth required placeholder="e.g., admin" />
            <TextField label="Name" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} size="small" fullWidth required placeholder="e.g., Administrator" />
            <TextField label="Description" value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} size="small" fullWidth multiline rows={2} />
            <Autocomplete
              multiple
              options={permissions}
              getOptionLabel={(option) => option.description || option.id}
              value={permissions.filter((p) => newRole.permissions.includes(p.id))}
              onChange={(_, newValue) => setNewRole({ ...newRole, permissions: newValue.map((p) => p.id) })}
              loading={permissionsLoading}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.description || option.id}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Permissions"
                  size="small"
                  placeholder={permissionsLoading ? 'Loading...' : 'Select permissions'}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateRole} disabled={creating || permissionsLoading || !newRole.roleId || !newRole.name}>
            {creating ? <Skeleton variant="text" width={60} height={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField label="Role ID" value={selectedRole?.roleId || ''} disabled size="small" fullWidth />
            <TextField 
              label="Name" 
              value={editName} 
              onChange={(e) => setEditName(e.target.value)} 
              size="small" 
              fullWidth 
              required 
            />
            <TextField 
              label="Description" 
              value={editDescription} 
              onChange={(e) => setEditDescription(e.target.value)} 
              size="small" 
              fullWidth 
              multiline 
              rows={2} 
            />
            <Autocomplete
              multiple
              options={permissions}
              getOptionLabel={(option) => option.description || option.id}
              value={permissions.filter((p) => selectedPermissions.includes(p.id))}
              onChange={(_, newValue) => setSelectedPermissions(newValue.map((p) => p.id))}
              loading={permissionsLoading}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.description || option.id}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Permissions"
                  size="small"
                  placeholder={permissionsLoading ? 'Loading...' : 'Select permissions'}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} disabled={updating}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRole} disabled={updating || permissionsLoading || !editName}>
            {updating ? <Skeleton variant="text" width={50} height={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Role?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={!!deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteRole} disabled={!!deleting}>
            {deleting ? <Skeleton variant="text" width={60} height={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={6000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastMessage(null)} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
